// @ts-check
import { AlertCircle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '../utils/apiClient';

/**
 * Lets an admin pick a previously-uploaded, already-compressed video instead of
 * re-uploading — e.g. after losing an unsaved selection to a page refresh. Backed by
 * GET /api/upload/jobs, for context="about_us" and "showcase".
 *
 * @param {{
 *   context: 'about_us' | 'showcase',
 *   onSelect: (url: string) => void,
 *   onCancel: () => void,
 * }} props
 */
const MediaJobPicker = ({ context, onSelect, onCancel }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`/api/upload/jobs?context=${encodeURIComponent(context)}`);
        if (!cancelled) setJobs(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load previous uploads');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [context]);

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="mt-3 border border-border rounded-lg p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h4 className="text-sm font-medium text-foreground/80">Choose a previously uploaded video</h4>
        <button onClick={onCancel} className="text-muted-foreground/70 hover:text-foreground">
          <X size={16} />
        </button>
      </div>

      {loading && <p className="text-xs text-muted-foreground">Loading…</p>}

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-md text-xs text-red-700 dark:text-red-300">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {!loading && !error && jobs.length === 0 && (
        <p className="text-xs text-muted-foreground">No previously uploaded videos found for this section yet.</p>
      )}

      {!loading && !error && jobs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
          {jobs.map((job) => (
            <button
              key={job.job_id}
              onClick={() => onSelect(job.url)}
              className="text-left group"
              title={job.original_filename}
            >
              <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden border-2 border-transparent group-hover:border-foreground transition-colors">
                <video src={job.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-foreground/80 mt-1 truncate">{job.original_filename}</p>
              <p className="text-xs text-muted-foreground/70">{formatDate(job.created_at)}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaJobPicker;
